import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

interface AuthContextType {
  user: User | null;
  role: 'admin' | 'editor' | 'viewer' | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SSO_VERIFY_URL = process.env.REACT_APP_SSO_VERIFY_URL || 'https://apps.iwa.web.tr/api/auth/verify';
const SSO_PORTAL_URL = process.env.REACT_APP_SSO_PORTAL_URL || 'https://apps.iwa.web.tr';
const APP_CODE = 'pricelab';

// FIX 13: Dev-only logging helper (no sensitive data in production)
const authLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'development') {
    logger.debug('[PriceLab Auth]', ...args);
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer' | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get token from URL or localStorage
  const getAccessToken = (): string | null => {
    // Check URL hash first (more secure), then query params for token (first login from SSO)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    const urlToken = hashParams.get('token') || queryParams.get('token');

    if (urlToken) {
      // Save token with both keys for compatibility
      localStorage.setItem('sso_access_token', urlToken);
      localStorage.setItem('accessToken', urlToken);
      authLog('Token received and saved');
      // Clean URL and hash
      window.history.replaceState({}, '', window.location.pathname);
      return urlToken;
    }

    // Try to get from localStorage (check both keys)
    let token = localStorage.getItem('sso_access_token');
    if (!token) {
      token = localStorage.getItem('accessToken');
      authLog('Using accessToken key');
    } else {
      authLog('Using sso_access_token key');
    }

    return token;
  };

  // Verify token with SSO backend
  const verifyToken = async (token: string): Promise<boolean> => {
    try {
      authLog('Verifying token...');
      // FIX 13: Token value NEVER logged

      const response = await fetch(SSO_VERIFY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, app_code: APP_CODE })
      });

      authLog('SSO response:', response.status);

      if (!response.ok) {
        authLog('SSO response not OK');
        return false;
      }

      const result = await response.json();
      // FIX 13: Response data NEVER logged (contains sensitive info)

      if (result.success) {
        const { user: userData, role: userRole, apps } = result.data;

        // FIX 13: User data NEVER logged
        // FIX 13: Removed for security
        // FIX 13: Apps list NEVER logged

        // Set user data
        setUser(userData);

        // Get role for this specific app
        const appRole = apps?.[APP_CODE] || userRole;
        setRole(appRole);

        authLog('Role:', appRole ? 'granted' : 'denied');
        authLog('Authentication successful');

        return true;
      }

      authLog('SSO response success=false');
      return false;
    } catch (error) {
      logger.error('[PriceLab Auth] Token verification failed:', error);
      return false;
    }
  };

  // Initialize authentication
  useEffect(() => {
    const initAuth = async () => {
      const token = getAccessToken();

      if (!token) {
        // No token - redirect to SSO
        authLog('No SSO token found, redirecting...');
        window.location.href = SSO_PORTAL_URL;
        return;
      }

      // Verify token
      const isValid = await verifyToken(token);

      if (!isValid) {
        // Invalid token - redirect to SSO
        authLog('Invalid SSO token, redirecting...');
        localStorage.removeItem('sso_access_token');
        window.location.href = SSO_PORTAL_URL;
        return;
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Logout function
  const logout = async () => {
    const token = localStorage.getItem('sso_access_token');

    // Notify SSO backend (optional)
    if (token) {
      try {
        await fetch(`${SSO_PORTAL_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        logger.error('Logout notification failed:', error);
      }
    }

    // FIX 14: Clear both token keys for complete logout
    localStorage.removeItem('sso_access_token');
    localStorage.removeItem('accessToken');

    // Redirect to SSO portal
    window.location.href = SSO_PORTAL_URL;
  };

  const value: AuthContextType = {
    user,
    role,
    isAuthenticated: !!user,
    isLoading,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
